import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { useQuery, queryOptions } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { getShopProduct } from "@/lib/shop.functions";
import { StripeEmbeddedShopCheckout } from "@/components/site/StripeEmbeddedShopCheckout";
import { ProductStreamPlayer } from "@/components/site/ProductStreamPlayer";
import { useOwnership } from "@/hooks/use-ownership";
import { supabase } from "@/integrations/supabase/client";

const productQuery = (slug: string) =>
  queryOptions({
    queryKey: ["shop", "product", slug],
    queryFn: () => getShopProduct({ data: { slug } }),
  });

export const Route = createFileRoute("/shop/$slug")({
  loader: async ({ context, params }) => {
    const product = await context.queryClient.ensureQueryData(productQuery(params.slug));
    if (!product) throw notFound();
    return product;
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [{ title: "Product · Buddha KA" }] };
    const title = `${loaderData.title} · Shop · Buddha KA`;
    const description = loaderData.subtitle ?? loaderData.long_description?.slice(0, 160) ?? "";
    const canonical = `https://buddhaka.com/shop/${params.slug}`;
    const price = (loaderData.amount_cents / 100).toFixed(2);
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "product" },
        { property: "og:url", content: canonical },
        ...(loaderData.image_url ? [{ property: "og:image", content: loaderData.image_url }] : []),
      ],
      links: [{ rel: "canonical", href: canonical }],
      scripts: [{
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          name: loaderData.title,
          description,
          ...(loaderData.image_url ? { image: loaderData.image_url } : {}),
          brand: { "@type": "Brand", name: "Buddha KA" },
          offers: {
            "@type": "Offer",
            url: canonical,
            priceCurrency: loaderData.currency.toUpperCase(),
            price,
            availability: "https://schema.org/InStock",
          },
        }),
      }],
    };
  },
  errorComponent: () => (
    <div className="mx-auto max-w-2xl px-4 py-24 text-center">
      <p className="text-sm text-muted-foreground">This artifact could not be loaded.</p>
    </div>
  ),
  notFoundComponent: () => (
    <div className="mx-auto max-w-2xl px-4 py-24 text-center">
      <p className="text-xs uppercase tracking-[0.3em] text-gold">Not found</p>
      <h1 className="mt-3 font-serif text-3xl">This artifact is not available.</h1>
      <Link to="/shop" className="mt-6 inline-block text-sm underline underline-offset-4">Return to the shop</Link>
    </div>
  ),
  component: ProductPage,
});

function formatPrice(cents: number, currency: string) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: currency.toUpperCase() }).format(cents / 100);
}

function ProductPage() {
  const { slug } = Route.useParams();
  const { data: product } = useQuery(productQuery(slug));
  const [buying, setBuying] = useState(false);
  const [authed, setAuthed] = useState<boolean | null>(null);
  const ownership = useOwnership();
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setAuthed(!!data.user));
  }, []);
  if (!product) return null;

  const p = product as typeof product & {
    member_amount_cents?: number | null;
    downloadable?: boolean | null;
    streamable?: boolean | null;
    included_with_membership?: boolean | null;
    active?: boolean | null;
  };

  const access = ownership.accessState(product.slug, {
    active: p.active ?? true,
    amount_cents: product.amount_cents,
    external_purchase_url: product.external_purchase_url,
  });
  const owned = access === "owned" || access === "included_with_membership";
  const external = !!product.external_purchase_url;
  const memberPrice =
    ownership.membership.active &&
    p.member_amount_cents &&
    p.member_amount_cents >= 50 &&
    p.member_amount_cents < product.amount_cents
      ? p.member_amount_cents
      : null;

  return (
    <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 sm:py-24">
      <Link to="/shop" className="text-xs uppercase tracking-[0.3em] text-muted-foreground hover:text-gold">← Shop</Link>

      <div className="mt-8 grid gap-10 lg:grid-cols-2">
        <div className="overflow-hidden rounded-xl border hairline bg-charcoal">
          <div className="aspect-square w-full bg-obsidian">
            {product.image_url ? (
              <img src={product.image_url} alt={product.title} className="h-full w-full object-cover" loading="eager" decoding="async" fetchPriority="high" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-xs uppercase tracking-[0.3em] text-muted-foreground">
                {product.kind}
              </div>
            )}
          </div>
        </div>

        <div>
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-[10px] uppercase tracking-[0.3em] text-gold">{product.kind}</p>
            {owned && (
              <span className="rounded-full border border-gold/50 px-3 py-1 text-[10px] uppercase tracking-[0.25em] text-gold">
                {access === "owned" ? "In your library" : "Included with membership"}
              </span>
            )}
          </div>
          <h1 className="mt-3 font-serif text-4xl">{product.title}</h1>
          {product.subtitle && <p className="mt-2 text-sm text-muted-foreground">{product.subtitle}</p>}

          {!owned && (
            <p className="mt-6 font-serif text-3xl text-gold-luminous">
              {external ? (
                "Sold externally"
              ) : memberPrice ? (
                <>
                  {formatPrice(memberPrice, product.currency)}{" "}
                  <span className="align-middle text-base text-muted-foreground line-through">
                    {formatPrice(product.amount_cents, product.currency)}
                  </span>
                  <span className="ml-2 align-middle text-xs uppercase tracking-[0.2em] text-gold">Member price</span>
                </>
              ) : (
                formatPrice(product.amount_cents, product.currency)
              )}
            </p>
          )}

          {product.long_description && (
            <div className="mt-8 whitespace-pre-line text-sm leading-relaxed text-muted-foreground">
              {product.long_description}
            </div>
          )}

          <div className="mt-10 space-y-4">
            {owned ? (
              <>
                {p.streamable && <ProductStreamPlayer slug={product.slug} />}
                <div className="flex flex-wrap gap-3">
                  <Link
                    to="/library"
                    className="inline-block rounded-full bg-gold-gradient px-6 py-3 text-sm font-semibold text-obsidian"
                  >
                    {p.downloadable && access === "owned" ? "Download in your library →" : "Open your library →"}
                  </Link>
                </div>
                {access === "included_with_membership" && p.downloadable && (
                  <p className="text-xs text-muted-foreground">
                    Membership includes streaming access. Purchase this product to keep the download files.
                  </p>
                )}
              </>
            ) : external ? (
              <a
                href={product.external_purchase_url!}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block rounded-full bg-gold-gradient px-6 py-3 text-sm font-semibold text-obsidian"
              >
                Buy on external site →
              </a>
            ) : access === "unavailable" ? (
              <p className="text-sm text-muted-foreground">This artifact is not currently available for purchase.</p>
            ) : buying ? (
              <StripeEmbeddedShopCheckout
                slug={product.slug}
                requiresShipping={!!product.requires_shipping}
                isAuthenticated={!!authed}
              />
            ) : (
              <button
                type="button"
                disabled={authed === null || ownership.isLoading}
                onClick={() => setBuying(true)}
                className="rounded-full bg-gold-gradient px-6 py-3 text-sm font-semibold text-obsidian transition hover:opacity-90 disabled:opacity-50"
              >
                Buy · {formatPrice(memberPrice ?? product.amount_cents, product.currency)}
              </button>
            )}
            {!owned && product.requires_shipping && (
              <p className="text-xs text-muted-foreground">
                Ships worldwide · shipping calculated at checkout
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

